import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import type { ReactNode } from 'react';
import { safeHref } from '../../lib/safe-url.js';

function SafeLink({ href, children, title }: { href?: string; children?: ReactNode; title?: string }) {
  const target = safeHref(href);
  if (!target) return <span>{children}</span>;
  const external = /^https?:/i.test(target);
  return (
    <a
      href={target}
      title={title}
      className="text-accent-text hover:underline"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

/** Images in generated content are shown as links, never fetched: a remote image is a tracking beacon. */
function InertImage({ src, alt }: { src?: string; alt?: string }) {
  const target = safeHref(src);
  const label = alt?.trim() ? `Image: ${alt}` : 'Image';
  if (!target) return <span className="text-muted">{label}</span>;
  return (
    <a href={target} target="_blank" rel="noopener noreferrer" className="text-accent-text hover:underline">
      {label}
    </a>
  );
}

export interface SafeMarkdownProps {
  children: string;
  /** Element styling (`h1`, `p`, `code`, …). Links and images are always the safe renderers. */
  overrides?: MarkdownToJSX.Overrides;
  forceBlock?: boolean;
}

/**
 * The one way to render markdown that came from data — model output, tool results, sources, help files.
 * Raw HTML inside the markdown is shown as text (never turned into elements), links go through `safeHref`
 * and open external sites without `opener`, and images are not loaded.
 */
export function SafeMarkdown({ children, overrides, forceBlock }: SafeMarkdownProps) {
  return (
    <Markdown
      options={{
        forceBlock,
        disableParsingRawHTML: true,
        overrides: { ...overrides, a: SafeLink, img: InertImage },
      }}
    >
      {children}
    </Markdown>
  );
}
